<template>
    <b-form-row v-if="patient">
        <b-col>
            <b-form-row cols-lg="2" cols="1" class="align-items-center">
                <b-col>
                    <SearchComponent v-model="selectedDiagnosis" label="Diagnosis Search" search-u-r-l="/Diagnosis"
                        :disabled="disable" clearable :results-modifier="formatResults" />
                </b-col>
                <b-col>
                    <label>Primary for Programs:</label>
                    <Multiselect v-model="selectedPrimaryPrograms" :options="programs" track-by="id"
                        label="name" multiple :disabled="disable" />
                </b-col>
            </b-form-row>

            <b-button @click="addDiagnosis" v-if="!disable" variant="success">Add Diagnosis</b-button>

            <b-table-simple v-show="patientDiagnosis.length > 0">
                <b-thead>
                    <b-tr>
                        <b-th colspan="2">Diagnosis</b-th>
                        <b-th class="primary-programs">Primary for Programs</b-th>
                        <b-th>Added Date</b-th>
                        <b-th class="remove"></b-th>
                    </b-tr>
                </b-thead>
                <b-tbody>
                    <b-tr v-for="diagnosis in patientDiagnosis" :key="diagnosis.icD10Code">
                        <b-td>{{ diagnosis.icD10Code }}</b-td>
                        <b-td>{{ diagnosis.icD10Description }}</b-td>
                        <b-td>
                            <Multiselect v-model="diagnosis.primaryPrograms" :options="programs" track-by="id"
                                label="name" multiple @input="updateDiagnosis(diagnosis)" />
                        </b-td>
                        <b-td>
                            {{ diagnosis.created | formatDate("LLL", !!diagnosis.isUtc) }}
                        </b-td>
                        <b-td>
                            <b-button variant="danger" @click="removeDiagnosis(diagnosis)" size="sm">
                                Remove
                            </b-button>
                        </b-td>
                    </b-tr>
                </b-tbody>
            </b-table-simple>
        </b-col>
    </b-form-row>
</template>

<script lang="ts">
import { Component, Watch, Prop, Vue } from 'vue-property-decorator';
import { DatePicker } from "element-ui";
import axios from 'axios';
import { Patient } from '@/models/Patient';

import SearchComponent from '@/components/SearchComponent.vue';
import NewClearSaveButtons from '@/components/NewClearSaveButtons.vue';
import AddressFormFields from '@/components/AddressFormFields.vue';
import Checkbox from "@/components/Inputs/Checkbox.vue";
import { GSDDPatientDiagnosis, ICD10Term } from "@/models/GSDD";
import { NotificationOptions } from "@/util/NotificationOptionsPresets";
import Multiselect from 'vue-multiselect';
import { Program } from '@/models/Program';

@Component({
    name: "GSDDDiagnosisComponent",
    components: {
        SearchComponent,
        NewClearSaveButtons,
        AddressFormFields,
        Checkbox,
        [DatePicker.name]: DatePicker,
        Multiselect
    }
})

export default class GSDDDiagnosisComponent extends Vue {
    @Prop({ default: null }) private patient!: Patient;
    @Prop({ default: false }) private disable!: boolean;
    @Prop({ default: () => [] }) private value!: GSDDPatientDiagnosis[];

    private selectedDiagnosis: ICD10Term = new ICD10Term();
    private selectedPrimaryPrograms: Program[] = [];

    protected programs: Program[] = [];

    get patientDiagnosis(): GSDDPatientDiagnosis[] {
        return this.value;
    }

    set patientDiagnosis(val: GSDDPatientDiagnosis[]) {
        this.$emit('input', val);
    }

    created() {
        this.getPatientGSDDDiagnosis();
        this.fetchPrograms();
    }

    async fetchPrograms() {
        try {
            const resp = await axios.get<Program[]>('/Program');
            this.programs = resp.data;
        } catch (err) {
            this.$notification(NotificationOptions.error(err));
        }
    }

    getPatientGSDDDiagnosis() {
        if (!this.patient?.id) return;

        axios.get(`/Diagnosis/Patient/${this.patient.id}`)
            .then(response => {
                this.patientDiagnosis = response.data.sort((a: GSDDPatientDiagnosis, b: GSDDPatientDiagnosis) => {
                    if (!a.created && !b.created) return 0;
                    if (!a.created) return 1;
                    if (!b.created) return -1;
                    return new Date(b.created).getTime() - new Date(a.created).getTime();
                });
            }).catch(error => {
                console.error("Error while loading diagnosis", { error, response: error?.response });
                this.$notification(NotificationOptions.error(error));
            });
    }

    @Watch('patient.id')
    onPatientChanged(_value: Patient | null, _oldValue: Patient | null) {
        this.clearComponent();
        this.getPatientGSDDDiagnosis();
    }

    async addDiagnosis() {
        if (!this.selectedDiagnosis || !this.selectedDiagnosis.icD10Code?.trim()) {
            this.$notification(NotificationOptions.error("Please select a Diagnosis."));
            return;
        }

        // Check if diagnosis exists
        const existingDiagnosis = this.patientDiagnosis.find(d => d.icD10Code === this.selectedDiagnosis.icD10Code);
        if (existingDiagnosis) {
            this.$notification(NotificationOptions.error("This Diagnosis has already been added."));
            return;
        }

        // If patient exists, save
        if (this.patient?.id) {
            const data = {
                icD10Code: this.selectedDiagnosis.icD10Code,
                primaryPrograms: this.selectedPrimaryPrograms || []
            }

            try {
                await axios.post(`/Diagnosis/AddToPatient/${this.patient.id}`, data);
                this.getPatientGSDDDiagnosis();
                this.$notification(NotificationOptions.success("Added diagnosis to patient"));
                this.selectedDiagnosis = new ICD10Term();
                this.selectedPrimaryPrograms = [];
            } catch (err) {
                this.$notification(NotificationOptions.error(err));
            }
        } else {
            // New patient mode
            const newDiagnosis = {
                icD10Code: this.selectedDiagnosis.icD10Code,
                icD10Description: this.selectedDiagnosis.icD10Description || '',
                primaryPrograms: [...(this.selectedPrimaryPrograms || [])],
                created: new Date() as any,
                patientId: 0, // Will be set when patient is saved
                id: 0, // Temporary ID for new diagnosis
                createdBy: '',
                removed: null as any,
                removedBy: ''
            } as GSDDPatientDiagnosis;

            this.patientDiagnosis = [...this.patientDiagnosis, newDiagnosis];
            this.selectedDiagnosis = new ICD10Term();
            this.selectedPrimaryPrograms = [];
        }
    }

    async removeDiagnosis(diagnosis: any) {
        if (this.patient?.id) {
            try {
                await axios.post(`/Diagnosis/RemoveFromPatient`, diagnosis);
                this.getPatientGSDDDiagnosis();
            } catch (err) {
                this.$notification(NotificationOptions.error(err));
            }
        } else {
            this.patientDiagnosis = this.patientDiagnosis.filter(d => d.icD10Code !== diagnosis.icD10Code);
        }
    }

    clearComponent() {
        this.patientDiagnosis = new Array<GSDDPatientDiagnosis>();
        this.selectedDiagnosis = new ICD10Term();
        this.selectedPrimaryPrograms = [];
    }

    formatResults(items: any) {
        const seen = new Set();
        return items.filter((item: any) => {
            const icD10Code = item.icD10Code;
            if (seen.has(icD10Code)) {
                return false;
            } else {
                seen.add(icD10Code);
                return true;
            }
        });
    }

    async updateDiagnosis(diagnosis: GSDDPatientDiagnosis) {
        if (this.patient?.id) {
            // Edit mode
            try {
                await axios.post(`/Diagnosis/Update`, diagnosis);
                this.getPatientGSDDDiagnosis();
                this.$notification(NotificationOptions.success("Updated diagnosis on patient"));
            } catch (err) {
                this.$notification(NotificationOptions.error(err));
            }
        } else {
            // New patient mode
            this.patientDiagnosis = [...this.patientDiagnosis];
        }
    }

}
</script>

<style scoped lang="scss">
th.remove {
    width: 110px;
}

th.primary-programs {
    width: 350px;
}
</style>
